// NOTE: used on Activity and Request pages
import * as React from "react"
import { useParams } from "react-router-dom"
import { useQueryClient } from "@tanstack/react-query"
import { Modal, Form, message } from "antd"
import PropTypes from "prop-types"
import { PrimeButton, TextInput } from "components-design-system"
import Checkbox from "components/Checkbox/Checkbox"
import SendEmailWorkflow from "components/WorkflowModalForm/views/SendEmailWorkflow/SendEmailWorkflow"
import SendActionItemWorkflow from "components/WorkflowModalForm/views/SendActionItemWorkflow/SendActionItemWorkflow"
import ReviewApprovalWorkflow from "components/WorkflowModalForm/views/ReviewApprovalWorkflow/ReviewApprovalWorkflow"

import "./WorkflowModalForm.scss"

// apis
import { useGetActivity } from "api/hooks/activities/useActivitiesApi"
import { useGetRequest } from "api/hooks/requests/useRequestsApi"
import { useGetWorkflow } from "api/hooks/workflows/useWorkflowsApi"
import { useGetAuthoringVersions } from "api/hooks/studies/useAuthoringApi" // version and version compare default values
import { usePostProgressWorkflow, usePostResend } from "api/hooks/workflows/useWorkflowsApi"

// utils
import useWorkflowTeam from "components/WorkflowModalForm/utils/useWorkflowTeam"
import useWorkflowTracker from "context/hooks/useWorkflowTracker"
import {
  expressCompleteSchema,
  requiredSchema,
  sendActionItemSchema,
  reviewApprovalSchema,
} from "components/WorkflowModalForm/utils/fieldValidation"
import { formatButtonText } from "./utils/formatButtonText"

const WorkflowModalForm = ({ open, setOpen, stepId, isResend, parentQueries }) => {
  const queryClient = useQueryClient()
  const { currentState } = useWorkflowTracker()

  // params and model
  const { studyId, activityId, requestId } = useParams()
  const model = !!activityId ? "activity" : "request"

  // server state
  const { data: activity } = useGetActivity(studyId, activityId, { enabled: model === "activity" })
  const { data: request } = useGetRequest(requestId, { enabled: model === "request" })
  const workflowId = activity ? activity?.workflow_id : request?.workflow_id
  const { data: workflowTracker } = useGetWorkflow({
    workflowId,
    workflowModel: model,
  })
  const { teamIds } = useWorkflowTeam()
  const { data: authoringVersions, refetch: refetchVersions } = useGetAuthoringVersions(activity?.authoring_study_pk) // version and version compare default values
  const postProgressWorkflow = usePostProgressWorkflow({ workflowId, workflowModel: model })
  const postResend = usePostResend({
    workflowId,
    successMsg: `Successfully resent to ${formatButtonText(currentState?.state_title)}`,
  })

  // derive server state

  // gets minor/major next action based on stepId passed to component
  const getNextAction = () => {
    if (!workflowTracker) return null
    return workflowTracker?.next_actions?.find((item) => item.id === stepId)
  }

  // for resend review modal
  const getPrevAction = () => {
    if (!workflowTracker) return null
    return workflowTracker?.prev_actions?.find((action) => action.id === stepId)
  }

  const prevAction = getPrevAction()
  const nextAction = getNextAction()

  // Undefined for minor steps
  const getNextTrackerState = () => {
    if (!workflowTracker) return null
    return workflowTracker?.state_data?.tracker_data.find((state) => state.state_id === nextAction?.next_state_id)
  }

  // refs
  const [form] = Form.useForm()
  const [messageApi, contextHolder] = message.useMessage() // TODO: this may be causing error when message not defined

  // state based on workflow configurations
  const willSendEmail = isResend ? prevAction?.send_email_notifications : nextAction?.send_email_notifications

  const willSendActionItem = isResend ? prevAction?.create_action_item : nextAction?.create_action_item
  const isReviewApproval = isResend
    ? !!(prevAction?.view_name === "Review") || !!(prevAction?.view_name === "Approve")
    : !!(nextAction?.view_name === "Review") || !!(nextAction?.view_name === "Approve")
  const [informTeam, setInformTeam] = React.useState(false)

  const [currentStep, setCurrentStep] = React.useState(0)
  const [actionItemDisabled, setActionItemDisabled] = React.useState(false)
  const [isExpressComplete, setIsExpressComplete] = React.useState(false)
  const [isSubmitting, setIsSubmitting] = React.useState(false)

  // reset for next workflow progression
  React.useEffect(() => {
    if (workflowTracker) {
      setCurrentStep(0)
      form.resetFields()
      setInformTeam(isResend ? prevAction?.inform_team : nextAction ? nextAction?.inform_team : true)
      setActionItemDisabled(false)
      setIsExpressComplete(false)
    }
  }, [open, workflowTracker, isResend]) // NOTE: important to reset on open

  // reset if sending express send
  React.useEffect(() => {
    if (form) {
      if (isExpressComplete) {
        // default values for express complete
        let setValues = {
          send_email_notification: false,
          attachment_docs: [],
          assign_to_ids: [],
          cc_emails: [],
          create_action_item: false,
          review_approve_data: {
            comment_types: [],
            form_types: [],
            form_version: null,
            documents: [],
          },
          inform_team: false,
        }
        // study_id only for "activity", not "request", workflows
        if (model === "activity") {
          setValues.review_approve_data.study_id = activity.study_uid
        }
        form.setFieldsValue(setValues)
      } else {
        form.resetFields()
      }
    }
  }, [form, isExpressComplete, model])

  // reset form type fields if user chooses not to send form for review

  const getInitialValues = () => {
    const initialValues = {
      model,
      action_id: isResend ? prevAction?.id : nextAction?.id,

      send_email_notification: !willSendEmail || isExpressComplete ? false : true,
      attachment_docs: [],

      // assign
      assign_to_ids:
        (isResend
          ? prevAction?.assigned_to_ids
          : getNextTrackerState()?.assigned_to_ids || nextAction?.assigned_to_ids) || [],
      cc_emails: isResend ? prevAction?.default_cc_email : nextAction?.default_cc_email || [],

      // action item
      create_action_item: willSendActionItem,
      actionitem_data: {
        title: isResend ? prevAction?.ai_title : nextAction?.ai_title,
        due_date: isResend
          ? // current tracker state's end_state_date is null
            currentState?.end_state_date?.split("T")?.[0]
          : getNextTrackerState()?.end_state_date?.split("T")?.[0],
        priority: 1,
      },

      // review_approve_data
      review_approve_data: {
        form_types: isResend ? prevAction?.form_types : nextAction?.form_types || [], // getting form types for resend review from the approval action
        form_version: authoringVersions?.map((version) => version.version_num)?.[0] || "",
        comment_types: ["Review"],
        documents: [],
      },

      // inform team
      inform_team: !!informTeam,
      inform_team_data: {
        inform_team_ids: teamIds,
        inform_team_cc: isResend ? prevAction?.default_inform_team_cc : nextAction?.default_inform_team_cc,
      },

      // comment text
      comment_data: {
        text: "",
        tags: [], // not on UI
      },

      cancel_action_item_ids: [],
    }

    if (model === "activity" && activity && authoringVersions) {
      initialValues.review_approve_data = {
        ...initialValues.review_approve_data,
        study_id: activity.study_uid,
        form_compare_version: authoringVersions?.map((version) => version.version_num)?.[1],
      }
    }

    return initialValues
  }

  const invalidateParentQueries = () => {
    if (parentQueries) {
      parentQueries.forEach((query) => {
        queryClient.invalidateQueries({ queryKey: query })
      })
    }
  }

  // when field is on UI but item not on payload
  const cleanPayload = (data) => {
    let cleanValues = { ...data }

    // if comment is empty string remove comment_data
    if (!cleanValues?.comment_data?.text) {
      delete cleanValues.comment_data
    }

    if (cleanValues.attachment_docs) {
      cleanValues.attachment_docs = cleanValues.attachment_docs.map((doc) => doc.value)
    }

    return cleanValues
  }

  const onFinish = (values) => {
    setIsSubmitting(true)
    const body = cleanPayload({ ...values })

    let zodResult = {}
    if (isExpressComplete) {
      zodResult = expressCompleteSchema.safeParse(body)
      console.log("zod expressCompleteSchema...")
    } else if (isReviewApproval) {
      zodResult = reviewApprovalSchema.safeParse(body)
      console.log("zod reviewApprovalSchema...")
    } else if (willSendActionItem) {
      zodResult = sendActionItemSchema.safeParse(body)
      console.log("zod sendActionItemSchema...")
    } else {
      zodResult = requiredSchema.safeParse(body)
      console.log("default requiredSchema...")
    }
    if (zodResult?.success) {
      console.log("zod SUCCESS", {
        body,
        zodResult,
      })
    } else {
      console.error("zod ERR", {
        body,
        zodResult,
        issues: zodResult?.error?.issues,
      })
    }

    if (isResend) {
      postResend(
        { body },
        {
          onSuccess: () => {
            invalidateParentQueries()
            if (model === "activity") refetchVersions()
            setOpen(false)
          },
          onSettled: () => {
            setIsSubmitting(false)
          },
        }
      )
    } else {
      postProgressWorkflow(
        { body },
        {
          onSuccess: () => {
            invalidateParentQueries()
            if (model === "activity") refetchVersions() // if (activity?.authoring_study_pk) refetchVersions()
            setOpen(false)
          },
          onSettled: () => {
            setIsSubmitting(false)
          },
        }
      )
    }
  }

  const getTitle = () => {
    if (isResend) return `Resend to ${formatButtonText(currentState?.state_title)}`
    if (getNextTrackerState()) return `Send to ${formatButtonText(getNextTrackerState()?.state_title)}`
    return formatButtonText(nextAction?.button_text)
  }

  // NOTE: show status on icons with status: "error" or status: "warning"
  const reviewApprovalStepItems = [
    { title: "Send email" },
    { title: model === "activity" ? "Select form & files" : "Select files" },
    { title: "Confirm & send" },
  ]

  const getSubmitLabel = () => {
    if (isExpressComplete) return "Complete"
    if (informTeam) return "Send"
    if (actionItemDisabled) return "Save"
    if (willSendEmail || willSendActionItem || isReviewApproval) return "Send"
    return "Save" // no action item and no email sent
  }

  return (
    <Modal
      title={
        <div className="workflow-modal-header">
          <div>{getTitle()}</div>
          {!isResend && (
            <Checkbox
              checked={isExpressComplete}
              onChange={(evt) => setIsExpressComplete(evt.target.checked)}
              disabled={isSubmitting}
              size="small"
            >
              Express complete
            </Checkbox>
          )}
        </div>
      }
      open={open}
      onCancel={() => setOpen(false)}
      maskClosable={false}
      footer={null}
      width={720}
      afterOpenChange={() => form.resetFields()} // reset to initial values when from switch between different worfklow modals, eg, major vs minor, next vs resend current
      className="workflow-modal-form"
      destroyOnClose={true}
    >
      <Form
        form={form}
        name="workflow-modal-form"
        onFinish={onFinish}
        initialValues={getInitialValues()}
        disabled={isSubmitting}
        layout="vertical"
        labelWrap
      >
        {/* HIDDEN FIELDS - required but not on UI */}

        {/* hidden fields for all config settings */}
        <Form.Item
          name="model"
          hidden
        />
        <Form.Item
          name="action_id"
          hidden
        />
        <Form.Item
          name="create_action_item"
          hidden
        />
        <Form.Item
          name="send_email_notification"
          hidden
        />
        <Form.Item
          name="cc_emails"
          hidden
        />
        {/* cancel_action_item_ids and tags are legacy fields no longer on UI */}
        <Form.Item
          name="cancel_action_item_ids"
          hidden
        />
        <Form.Item
          name={["comment_data", "tags"]}
          hidden
        />

        {/* hidden fields for request model or express complete */}
        {((model === "request" && isReviewApproval) || isExpressComplete) && (
          <>
            <Form.Item
              name={["review_approve_data", "comment_types"]}
              hidden
            />
            <Form.Item
              name={["review_approve_data", "form_types"]}
              hidden
            />
            <Form.Item
              name={["review_approve_data", "form_version"]}
              hidden
            />
          </>
        )}

        {/* hidden fields - more for express complete */}
        {isExpressComplete && (
          <>
            {/* hidden fields */}
            <Form.Item
              name="assign_to_ids"
              hidden
            />
            <Form.Item
              name={["review_approve_data", "documents"]}
              hidden
            />
            <Form.Item
              name="inform_team"
              hidden
            />
            <Form.Item
              name="attachment_docs"
              hidden
            />
            {model === "activity" && (
              <Form.Item
                name={["review_approve_data", "study_id"]}
                hidden
              />
            )}
          </>
        )}

        {/* CONFIG FIELDS */}

        {isExpressComplete && (
          <Form.Item
            name={["comment_data", "text"]}
            label="Comments"
          >
            <TextInput
              type="textarea"
              disabled={isSubmitting}
              allowClear
              autoSize={{ minRows: 3, maxRows: 4 }}
            />
          </Form.Item>
        )}

        {/* review/approval config */}
        {/* TODO: move form view hidden fields from ReviewApprovalWorkflow here */}
        {!isExpressComplete && isReviewApproval && (
          <ReviewApprovalWorkflow
            form={form}
            stepItems={reviewApprovalStepItems}
            isResend={isResend}
            currentStep={currentStep}
            setCurrentStep={setCurrentStep}
            actionItemDisabled={actionItemDisabled}
            setActionItemDisabled={setActionItemDisabled}
            informTeam={informTeam}
            setInformTeam={setInformTeam}
            disabled={isSubmitting}
          />
        )}

        {/* send action item config (not review/approval) */}
        {!isExpressComplete && !isReviewApproval && willSendActionItem && (
          <div className="card-wrap">
            <div className="workflow-card-wrap">
              <SendActionItemWorkflow
                form={form}
                actionItemDisabled={actionItemDisabled}
                setActionItemDisabled={setActionItemDisabled}
                sendEmail={willSendEmail}
                informTeam={informTeam}
                setInformTeam={setInformTeam}
              />
              {/* comments */}
              <Form.Item
                name={["comment_data", "text"]}
                label="Comments"
                className="workflow-modal-comment"
              >
                <TextInput
                  type="textarea"
                  disabled={isSubmitting}
                  allowClear
                  autoSize={{ minRows: 3, maxRows: 4 }}
                />
              </Form.Item>
            </div>
          </div>
        )}

        {/* no config or email-only config */}
        {!isExpressComplete && !isReviewApproval && !willSendActionItem && (
          <div className="card-wrap">
            <div className="workflow-card-wrap">
              <SendEmailWorkflow
                form={form}
                sendEmail={willSendEmail}
                informTeam={informTeam}
                setInformTeam={setInformTeam}
              />

              {/* comments */}
              <Form.Item
                name={["comment_data", "text"]}
                label="Comments"
                className="workflow-modal-comment"
              >
                <TextInput
                  type="textarea"
                  disabled={isSubmitting}
                  allowClear
                  autoSize={{ minRows: 3, maxRows: 4 }}
                />
              </Form.Item>
            </div>

            {/* hidden required fields - no cc field on UI */}
            <Form.Item
              name="cc_emails"
              hidden
            />
            <Form.Item
              name="attachment_docs"
              hidden
            />
          </div>
        )}

        {/* footer */}
        <div className="workflow-modal-footer">
          {/* back button */}
          {currentStep !== 0 && !isExpressComplete && (
            <PrimeButton onClick={() => setCurrentStep(currentStep - 1)}>Back</PrimeButton>
          )}

          {/* next/submit button */}
          {isExpressComplete || !isReviewApproval || currentStep === reviewApprovalStepItems.length - 1 ? (
            <PrimeButton
              type="primary"
              htmlType="button"
              loading={isSubmitting}
              onClick={() => {
                // show message if not validated
                const showErrMsg = () => {
                  messageApi.open({
                    type: "error",
                    content: "Please fill out required fields before submitting form",
                  })
                }
                form
                  .validateFields()
                  .then(() => {
                    form.submit()
                  })
                  .catch((errorInfo) => {
                    console.log("errorInfo", errorInfo)
                    showErrMsg()
                  })
              }}
              className="workflow-modal-submit"
            >
              {getSubmitLabel()}
            </PrimeButton>
          ) : (
            <Form.Item noStyle>
              <PrimeButton
                type="primary"
                onClick={() => setCurrentStep(currentStep + 1)}
                htmlType="button"
                className="workflow-modal-submit"
              >
                Next
              </PrimeButton>
            </Form.Item>
          )}
        </div>
      </Form>
      {contextHolder}
    </Modal>
  )
}

WorkflowModalForm.propTypes = {
  open: PropTypes.bool.isRequired,
  setOpen: PropTypes.func.isRequired,
  stepId: PropTypes.number,
  isResend: PropTypes.bool,
  parentQueries: PropTypes.arrayOf(PropTypes.array),
}

export default WorkflowModalForm
